import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { AnimatePresence, m } from 'framer-motion';
import { useConsent } from '../../context/ConsentContext';
import { Button, Modal, Toggle } from '../ui';

// "Reject all" is as easy to reach as "Accept all" (CNIL guidance, 2020-091).
// Nothing optional runs until the visitor chooses.
export default function CookieBanner() {
  const { decided, acceptAll, rejectAll, save, settingsOpen, openSettings, closeSettings, choice } = useConsent();
  const [analytics, setAnalytics] = useState(!!choice?.analytics);
  useEffect(() => { if (settingsOpen) setAnalytics(!!choice?.analytics); }, [settingsOpen, choice]);

  return (
    <>
      <AnimatePresence>
        {!decided && !settingsOpen && (
          <m.div role="region" aria-label="Cookie choices"
            initial={{ y: 24, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 24, opacity: 0 }}
            transition={{ type: 'spring', bounce: 0, duration: 0.4 }}
            className="fixed inset-x-3 bottom-3 z-[90] sm:inset-x-auto sm:bottom-5 sm:left-5 sm:max-w-md">
            <div className="rounded-4xl bg-surface p-5 shadow-float ring-1 ring-line sm:p-6">
              <p className="font-semibold text-ink">Your privacy, your choice</p>
              <p className="mt-2 text-sm leading-relaxed text-ink-muted">
                We use essential storage to keep you signed in. With your permission we’d also use Google Analytics to see which pages
                help people most. We don’t use advertising cookies or session recording. <Link to="/cookies" className="font-medium text-ink underline">Details</Link>
              </p>
              <div className="mt-5 grid grid-cols-2 gap-2">
                <Button variant="secondary" size="sm" onClick={rejectAll}>Reject all</Button>
                <Button size="sm" onClick={acceptAll}>Accept all</Button>
              </div>
              <button type="button" onClick={openSettings} className="mt-3 w-full text-center text-sm font-medium text-ink-muted underline-offset-4 hover:text-ink hover:underline">
                Choose what to allow
              </button>
            </div>
          </m.div>
        )}
      </AnimatePresence>

      <Modal open={settingsOpen} onClose={closeSettings} title="Cookie settings"
        description="You can change these choices at any time from the link in the footer."
        footer={(
          <>
            <Button variant="secondary" onClick={rejectAll}>Reject all</Button>
            <Button onClick={() => save({ analytics })}>Save choices</Button>
          </>
        )}>
        <div className="space-y-6">
          <Toggle checked disabled onChange={() => {}} label="Essential (always on)"
            description="Keeps you signed in, remembers this choice and protects forms from abuse. The site can’t work without these." />
          <Toggle checked={analytics} onChange={setAnalytics} label="Analytics"
            description="Google Analytics 4 with IP anonymisation, no advertising features and no cross-site tracking. Helps us understand which pages are useful. Its cookies expire after 13 months at most." />
          <p className="text-xs text-ink-muted">We never use session replay, keystroke recording or advertising cookies. See our <Link to="/cookies" className="underline">cookie policy</Link>.</p>
        </div>
      </Modal>
    </>
  );
}
